// eslint-disable-next-line @typescript-eslint/ban-ts-comment -- Legacy
// @ts-nocheck
// This is disabled because we want ts files but didn't have time to add the types
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons'
import cx from 'classnames'
import { FormattedMessage } from 'locales/index'
import PropTypes from 'prop-types'
import React, { Children, cloneElement, isValidElement, useMemo } from 'react'
import styles from './styles.module.scss'

// Thanks to Kent C. Dodds for the inspiration:
// https://kentcdodds.com/blog/compound-components-with-react-hooks
const StepsContext = React.createContext()

function Steps({ children, current, onChange, total, progress }) {
  const containerRef = React.useRef(null)
  const value = React.useMemo(() => ({ currentStep: current }), [current])
  const stepsNames = useMemo(
    () =>
      Children.toArray(children).map((child) =>
        isValidElement(child) && child.type === Step ? child.props.title : undefined
      ),
    [children]
  )
  const currentIndex = current - 1

  const handleOnPrevious = (e) => {
    e.preventDefault()
    if (onChange) {
      onChange(current - 1)
    }
  }

  const handleKeyDown = (e) => {
    if (e.keyCode === 13) {
      handleOnPrevious(e)
    }
  }

  React.useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView(true)
    }
  }, [current])

  const hasPreviousStep = stepsNames && stepsNames.length > 0 && stepsNames[currentIndex - 1]
  return (
    <StepsContext.Provider value={value}>
      <div ref={containerRef} className={cx(styles.steps, 'u-padding--large')} data-testid="steps-container">
        <div className={cx(styles['steps-header'], 'u-margin-bottom--medium')}>
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- Consider refactor */}
          <div
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- Consider refactor
            tabIndex={hasPreviousStep ? 0 : -1}
            onKeyDown={handleKeyDown}
            onClick={handleOnPrevious}
            className={styles['steps-header-title']}
          >
            {hasPreviousStep ? (
              <span>
                <FontAwesomeIcon icon={faArrowLeft} className="u-margin-right--small" />
                {stepsNames[currentIndex - 1]}
              </span>
            ) : null}
          </div>

          {progress ?? (
            <div className={styles['steps-header-progress']}>
              <div className="u-margin-horizontal--large u-margin-vertical--small">
                <FormattedMessage
                  id="components.steps.progress.title"
                  values={{ current: intl.formatNumber(current), total: intl.formatNumber(total) }}
                />
              </div>
            </div>
          )}
        </div>
        {Children.map(children, (child, index) => {
          if (!child || !isValidElement(child)) {
            return null
          }
          const childProps = {
            stepNumber: index + 1,
            ...child.props
          }
          return cloneElement(child, childProps)
        })}
      </div>
    </StepsContext.Provider>
  )
}

Steps.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  current: PropTypes.number,
  total: PropTypes.number,
  onChange: PropTypes.func,
  // Use composition to overwrite the progress component. This way we leave the control to
  // the client of this Steps component
  progress: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node])
}

const useStepsContext = () => {
  const context = React.useContext(StepsContext)
  if (!context) {
    throw new Error('Step compound components cannot be rendered outside the Steps component')
  }
  return context
}

function Step({ children, stepNumber }) {
  const { currentStep } = useStepsContext()
  return currentStep === stepNumber ? children : null
}
Step.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  stepNumber: PropTypes.number,
  // (it's used but indirectly -> child.props.title)

  // eslint-disable-next-line react/no-unused-prop-types -- Legacy
  title: PropTypes.node
}

Steps.Step = Step
export default Steps
